import { useState, useEffect } from 'react';
import update from 'immutability-helper';
import request from 'superagent';
import Analytics from '@lib/analytics';
import { requestScriptLoad } from '@components/helpers/externalScriptHelper';
import { Routes, Route, useNavigate, useLocation } from 'react-router';
import { snakeCaseKeys, toQueryString } from '@javascript/lib/utils';
import {
  AdditionalView,
  AddressView,
  ContactView,
  NameView,
  NoLongerAvailableView,
  PaymentView,
  WorldpayUnreachable,
} from './views';
import ProgressDisplay from '@components/application/progressDisplay';
import ReservationHeader from '../reservationHeader';
import { Vehicle } from '@sharedTypes/Vehicle';
import { Enquiry } from '@sharedTypes/Enquiry';

export interface ReserveNowContainerProps {
  authenticityToken: string;
  customerLocation: string;
  photo: string;
  quoteCode: string | null;
  quoteToken: string | null;
  stockReference: string;
  vehicle: Vehicle;
  vehicleUrl: string;
  worldpayHelper: string;
}

const ReserveNowContainer = ({
  authenticityToken,
  customerLocation,
  photo,
  quoteCode,
  quoteToken,
  stockReference,
  vehicle,
  vehicleUrl,
  worldpayHelper,
}: ReserveNowContainerProps): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState<Enquiry>({
    customerDetails: {
      address1: '',
      city: '',
      postcode: '',
      contact_email: '',
      contact_number: '',
      first_name: '',
      last_name: '',
      shopper_id: '',
      worldpay_url: '',
      comments: '',
      address2: '',
      state: { value: '' },
    },
    dealbuilder: !!quoteCode,
    finance: {},
    interestedInFinance: false,
    quoteCode,
    quoteToken,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const generateRoute = (routeName: string) => {
    return quoteCode && quoteToken
      ? `${routeName}/${stockReference}/${quoteCode}/${quoteToken}`
      : `${routeName}/${stockReference}`;
  };

  const steps: { name: string; route: string }[] = [
    {
      name: 'Your Name',
      route: generateRoute('/new'),
    },
    {
      name: 'Your Contact Details',
      route: generateRoute('/contact-details'),
    },
    {
      name: 'Your Comments',
      route: generateRoute('/comments'),
    },
    {
      name: 'Your Address',
      route: generateRoute('/address'),
    },
    {
      name: 'Your Payment Details',
      route: generateRoute('/payment'),
    },
    {
      name: 'Worldpay Unreachable',
      route: generateRoute('/payment-unavailable'),
    },
    {
      name: 'No longer available',
      route: generateRoute('/no-longer-available'),
    },
  ];

  useEffect(() => {
    requestScriptLoad('worldpay');
  }, []);

  useEffect(() => {
    requestScriptLoad('google');
  }, []);

  const stepIndex: number = steps.findIndex(
    (step) => step.route.toUpperCase() === location.pathname.toUpperCase(),
  );

  const nextView = (): void => {
    navigate(steps[stepIndex + 1].route);
  };

  const setStep = (route: string) => {
    navigate(generateRoute(route));
  };

  const updateCustomerDetails = (
    data: object,
    callback?: (customerDetails: object) => void,
  ) => {
    const newCustomerDetails = update(enquiry.customerDetails, {
      $merge: data,
    });

    setEnquiry((prevState) => {
      return {
        ...prevState,
        customerDetails: { ...prevState.customerDetails, ...data },
      };
    });

    if (callback) callback({ ...enquiry, customerDetails: newCustomerDetails });
  };

  const prepareEnquiry = (enquiry: Enquiry) => {
    return {
      ...snakeCaseKeys(enquiry.customerDetails),
      collection_point: '',
      quote_code: quoteCode,
      quote_token: quoteToken,
    };
  };

  const checkReserved = () => {
    request
      .get(`/api/reservation/${stockReference}`)
      .then((response) => {
        if (!response.body?.reserved) return;

        setError(true);
        setStep('no-longer-available');
      })
      .catch((err) => {
        if (!err) return;

        setError(true);
        setStep('no-longer-available');
      });
  };

  const redirectToThankYouPage = () => {
    const params = toQueryString({
      shopper_id: enquiry.customerDetails.shopper_id,
      stock_reference: stockReference,
    });

    Analytics.trackEvent('conversion', {
      action: 'Created reservation',
      label: 'Reserve now',
    });
    window.location.href = `/contact-us/thank-you/deal-builder/reserved?${params}`;
  };

  const redirectToErrorPage = () => {
    window.location.href = `/contact-us/thank-you/deal-builder/error?stock_reference=${stockReference}`;
  };

  const exitReserveJourney = () => {
    window.location.href = vehicleUrl;
  };

  const sendEnquiry = (enquiry: object) => {
    setLoading(true);
    request
      .post(`/contact-us/vehicle-enquiry/create-reservation/${stockReference}`)
      .send({
        authenticity_token: authenticityToken,
        reserve_now: true,
        enquiry: prepareEnquiry(enquiry as Enquiry),
      })
      .then((response) => {
        setLoading(false);
        if (response.ok && response.body.url) {
          updateCustomerDetails({
            shopper_id: response.body.shopper_id,
            worldpay_url: response.body.url,
          });
          setStep('payment');
        } else {
          setStep('payment-unavailable');
        }
      })
      .catch((err) => {
        if (window.newrelic) {
          window.newrelic.noticeError(err);
        }
        return redirectToErrorPage();
      });
  };

  return (
    <>
      <div className="ch-bg--white reserve-now__header">
        <button
          className="ac-exit-button ch-ba--0 ch-float--right ch-color--ac-black ch-bg--grey-2 ch-rounded--sm ch-fs--2 ch-fw--500 ch-ma--4"
          onClick={exitReserveJourney}
          type="button"
        >
          Exit
        </button>
      </div>
      <div className="ch-bg--white">
        {!error && (
          <ProgressDisplay
            currentStep={stepIndex + 1}
            exit={vehicleUrl}
            stepName="Reservation"
            stepped
            totalSteps={6}
          />
        )}
      </div>

      <div className="db-body">
        <ReservationHeader photo={photo} vehicle={vehicle} />
        <hr className="ch-mb--4 ch-bg--grey-3 ch-width--12 sm:ch-display--none" />
        <Routes>
          <Route
            element={
              <NameView
                customerDetails={enquiry.customerDetails}
                nextView={nextView}
                updateCustomerDetails={updateCustomerDetails}
              />
            }
            path={generateRoute('/new')}
          />
          <Route
            element={
              <ContactView
                customerDetails={enquiry.customerDetails}
                nextView={nextView}
                updateCustomerDetails={updateCustomerDetails}
              />
            }
            path={generateRoute('/contact-details')}
          />
          <Route
            element={
              <AdditionalView
                customerDetails={enquiry.customerDetails}
                nextView={nextView}
                updateCustomerDetails={updateCustomerDetails}
              />
            }
            path={generateRoute('/comments')}
          />
          <Route
            element={
              <AddressView
                customerDetails={enquiry.customerDetails}
                customerLocation={customerLocation}
                loading={loading}
                sendEnquiry={sendEnquiry}
                updateCustomerDetails={updateCustomerDetails}
              />
            }
            path={generateRoute('/address')}
          />
          <Route
            element={
              <PaymentView
                checkReserved={checkReserved}
                completeEnquiry={redirectToThankYouPage}
                enquiry={enquiry}
                stockReference={stockReference}
                worldpayHelper={worldpayHelper}
              />
            }
            path={generateRoute('/payment')}
          />
          <Route
            element={
              <WorldpayUnreachable
                authenticityToken={authenticityToken}
                enquiry={enquiry}
                prepareEnquiry={prepareEnquiry}
                redirectToErrorPage={redirectToErrorPage}
                stockReference={stockReference}
              />
            }
            path={generateRoute('/payment-unavailable')}
          />
          <Route
            element={<NoLongerAvailableView />}
            path={generateRoute('/no-longer-available')}
          />
        </Routes>
      </div>
    </>
  );
};

export default ReserveNowContainer;
